"use client";

import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import { ChevronDown } from "lucide-react";
import * as React from "react";
import { cn } from "@/shared/lib/cn";

function NavigationMenu({
  className,
  children,
  viewport = true,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Root> & {
  /** Shared morphing viewport (anchors panels to the menu root) vs
   *  per-trigger anchored panels. Left-aligned navs want `false` so
   *  each panel opens under its own trigger — position derives from
   *  the trigger, never from coordinates. */
  viewport?: boolean;
}) {
  const rootRef = React.useRef<HTMLDivElement | null>(null);

  // Stripe-style glide: the shared viewport follows the ACTIVE trigger.
  // On every open/change we measure the open trigger's offset within the
  // root and publish it as --nav-trigger-left; the viewport wrapper binds
  // its `left` to that var with a transition, so the panel slides between
  // triggers while Radix morphs its size. Position always derives from
  // the live DOM — re-measured on value change and window resize.
  const measure = React.useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const trigger = root.querySelector<HTMLElement>(
      '[data-slot="navigation-menu-trigger"][data-state="open"]',
    );
    if (trigger) {
      const left = trigger.getBoundingClientRect().left - root.getBoundingClientRect().left;
      root.style.setProperty("--nav-trigger-left", `${left}px`);
    }
  }, []);

  React.useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  return (
    <NavigationMenuPrimitive.Root
      ref={rootRef}
      data-slot="navigation-menu"
      data-viewport={viewport}
      onValueChange={() => {
        // Radix flips data-state after this callback — measure post-commit.
        requestAnimationFrame(measure);
      }}
      className={cn(
        "group/navigation-menu relative z-10 flex max-w-max flex-1 items-center justify-center",
        className,
      )}
      {...props}
    >
      {children}
      {viewport && <NavigationMenuViewport />}
    </NavigationMenuPrimitive.Root>
  );
}

function NavigationMenuList({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.List>) {
  return (
    <NavigationMenuPrimitive.List
      data-slot="navigation-menu-list"
      className={cn("group flex flex-1 list-none items-center justify-center gap-1", className)}
      {...props}
    />
  );
}

function NavigationMenuItem({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Item>) {
  return (
    <NavigationMenuPrimitive.Item
      data-slot="navigation-menu-item"
      className={cn("relative", className)}
      {...props}
    />
  );
}

function NavigationMenuTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Trigger>) {
  return (
    <NavigationMenuPrimitive.Trigger
      data-slot="navigation-menu-trigger"
      className={cn(
        "group inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm text-foreground/80 transition-colors duration-150 hover:bg-[color-mix(in_oklch,var(--color-primary)_7%,transparent)] hover:text-foreground focus-visible:outline-none data-[state=open]:bg-[color-mix(in_oklch,var(--color-primary)_7%,transparent)] data-[state=open]:text-foreground",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronDown
        className="size-3.5 opacity-60 transition-transform duration-200 group-data-[state=open]:rotate-180"
        aria-hidden
      />
    </NavigationMenuPrimitive.Trigger>
  );
}

function NavigationMenuContent({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Content>) {
  return (
    <NavigationMenuPrimitive.Content
      data-slot="navigation-menu-content"
      className={cn(
        "data-[motion^=from-]:animate-in data-[motion^=to-]:animate-out data-[motion^=from-]:fade-in data-[motion^=to-]:fade-out data-[motion=from-end]:slide-in-from-right-8 data-[motion=from-start]:slide-in-from-left-8 data-[motion=to-end]:slide-out-to-right-8 data-[motion=to-start]:slide-out-to-left-8 left-0 top-0 p-2 md:absolute md:w-auto",
        "group-data-[viewport=false]/navigation-menu:top-full group-data-[viewport=false]/navigation-menu:mt-2 group-data-[viewport=false]/navigation-menu:overflow-hidden group-data-[viewport=false]/navigation-menu:rounded-2xl group-data-[viewport=false]/navigation-menu:border group-data-[viewport=false]/navigation-menu:border-border/60 group-data-[viewport=false]/navigation-menu:bg-popover/95 group-data-[viewport=false]/navigation-menu:text-popover-foreground group-data-[viewport=false]/navigation-menu:backdrop-blur-xl group-data-[viewport=false]/navigation-menu:shadow-overlay group-data-[viewport=false]/navigation-menu:duration-200 group-data-[viewport=false]/navigation-menu:data-[state=open]:animate-in group-data-[viewport=false]/navigation-menu:data-[state=closed]:animate-out group-data-[viewport=false]/navigation-menu:data-[state=open]:fade-in group-data-[viewport=false]/navigation-menu:data-[state=closed]:fade-out group-data-[viewport=false]/navigation-menu:data-[state=open]:zoom-in-95 group-data-[viewport=false]/navigation-menu:data-[state=closed]:zoom-out-95",
        className,
      )}
      {...props}
    />
  );
}

function NavigationMenuViewport({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Viewport>) {
  // No width on this wrapper (canonical shadcn): it must shrink-to-fit
  // the Viewport's measured --radix-…-viewport-width. w-full pins it to
  // the Root's max-w-max (just the nav links), and since the Viewport is
  // a flex child with overflow-hidden (automatic min size → 0),
  // flex-shrink crushes the panel to that width and clips the grid.
  return (
    <div
      className="absolute top-full isolate z-50 flex justify-center transition-[left] duration-150 ease-out"
      style={{ left: "var(--nav-trigger-left, 0px)" }}
    >
      <NavigationMenuPrimitive.Viewport
        data-slot="navigation-menu-viewport"
        className={cn(
          // The shared morphing panel — the signature sleek-SaaS feel.
          "origin-top-center relative mt-2 h-[var(--radix-navigation-menu-viewport-height)] w-full overflow-hidden rounded-2xl border border-border/60 bg-popover/95 text-popover-foreground backdrop-blur-xl transition-[width,height] duration-150 md:w-[var(--radix-navigation-menu-viewport-width)]",
          "shadow-overlay",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          className,
        )}
        {...props}
      />
    </div>
  );
}

function NavigationMenuLink({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Link>) {
  return (
    <NavigationMenuPrimitive.Link
      data-slot="navigation-menu-link"
      className={cn("focus-visible:outline-none", className)}
      {...props}
    />
  );
}

export {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuViewport,
};
